import * as stylex from "@stylexjs/stylex";
import {
  spacing,
  colors,
  fontSizes,
  radii,
} from "@softmax/design-system/tokens.stylex";
import { ordinal } from "@softmax/product-ux/play-values";
import { barWidthOf, type DisplayEpisode } from "./episode-values";

export function EpisodeResult({
  row,
  narrow = false,
}: {
  readonly row: DisplayEpisode;
  readonly narrow?: boolean;
}) {
  if ("mySeatRank" in row) {
    const rank = (
      <span>
        {ordinal(row.mySeatRank)} of {row.seats}
      </span>
    );
    return narrow ? (
      rank
    ) : (
      <span {...stylex.props(localStyles.result)}>
        <span {...stylex.props(localStyles.rank)}>{rank}</span>
        <span aria-hidden="true" {...stylex.props(localStyles.bar)}>
          <span
            {...stylex.props(
              localStyles.fill(
                barWidthOf({ score: row.score, maxScore: row.maxScore }),
              ),
            )}
          />
        </span>
      </span>
    );
  }

  const tops = row.seats.filter(
    (seat) => seat.score !== null && seat.score === row.topScore,
  );
  const [top] = tops;
  const seats = `${row.seats.length} seat${row.seats.length === 1 ? "" : "s"}`;
  return (
    <span {...stylex.props(localStyles.topSeats)}>
      {top ? (
        <span {...stylex.props(localStyles.topName)}>
          {tops.length === 1 ? top.label : `${tops.length} tied`}
        </span>
      ) : null}
      <span {...stylex.props(localStyles.seatCount)}>
        {top ? `of ${seats}` : seats}
      </span>
    </span>
  );
}

const localStyles = stylex.create({
  topSeats: {
    display: "flex",
    alignItems: "baseline",
    gap: spacing.space6,
    minWidth: 0,
  },
  topName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: fontSizes.label,
  },
  seatCount: {
    flexShrink: 0,
    color: colors.muted,
    fontSize: fontSizes.caption,
  },
  result: {
    display: "flex",
    alignItems: "center",
    gap: spacing.space12,
    fontSize: fontSizes.label,
    lineHeight: "19px",
    minWidth: 0,
  },
  rank: { width: 80, flexShrink: 0 },
  bar: {
    display: { default: "none", "@container (min-width: 560px)": "block" },
    width: 64,
    height: 3,
    flexShrink: 0,
    borderRadius: radii.pill,
    backgroundColor: colors.border,
  },
  fill: (width: number) => ({
    display: "block",
    height: 3,
    width,
    borderRadius: radii.pill,
    backgroundColor: colors.foreground,
  }),
});
